import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import SearchIcon from '@material-ui/icons/Search';
import { ROUTE_EZ_SEARCH } from '../../../../constants/routes';
import useBooleanHandlers from '../../../../hooks/useBooleanHandlers';
import { useTrans } from '../../../../services/i18n';
import { emptyArray } from '../../../../utils/arrayUtils';
import { BackDrop, Paper, SpacingContainer } from '../../../common/Containers';
import Loader from '../../../common/Loader';
import Typography from '../../../common/Typography';
import { useMediaQueryDown } from '../../../../hooks/useMediaQuery';
import { isNaturalLanguageSearchEnabled } from '../../../../utils/naturalLanguage';
import SparklesIcon from '../../../generics/NaturalLanguageSearch/SparklesIcon';
import useStyles from '../styles';
import connect from './connect';

function EZSearch({
  text = '',
  searchMode = 'keyword',
  pending = false,
  recentSearches = [],
  onChangeText,
  onSetSearchMode,
  onFetchAiSearch,
  fetchRecentSearchs,
  setDrawerClose = () => {},
}) {
  const styles = useStyles();
  const { trans } = useTrans();
  const history = useHistory();
  const location = useLocation();
  const [focused, onFocus, onBlur] = useBooleanHandlers();
  const hasRecentSearches = !emptyArray(recentSearches);
  const isSmallScreen = useMediaQueryDown('sm');
  const nlEnabled = isNaturalLanguageSearchEnabled();

  const [inputValue, setInputValue] = useState(text || '');

  // Synchronize input with Redux text (keyword + AI fulfill) and URL deep links
  useEffect(() => {
    if (location.pathname === ROUTE_EZ_SEARCH.path) {
      const urlQ = new URLSearchParams(location.search).get('q');
      if (urlQ) {
        setInputValue(urlQ);
        return;
      }
    }
    setInputValue(text || '');
  }, [text, location.pathname, location.search]);

  // Synchronize mode from URL query parameters if present on /search
  useEffect(() => {
    if (location.pathname === ROUTE_EZ_SEARCH.path) {
      const searchParams = new URLSearchParams(location.search);
      const urlMode = searchParams.get('mode');
      if (urlMode === 'ai' && searchMode !== 'ai' && nlEnabled) {
        onSetSearchMode?.('ai');
      } else if (urlMode === 'keyword' && searchMode !== 'keyword') {
        onSetSearchMode?.('keyword');
      }
    }
  }, [location.pathname, location.search, searchMode, nlEnabled, onSetSearchMode]);

  const isAiMode = nlEnabled && searchMode === 'ai';

  const handleModeChange = useCallback(
    (newMode) => {
      if (newMode === searchMode) return;
      onSetSearchMode?.(newMode);
    },
    [searchMode, onSetSearchMode]
  );

  const handleSubmit = useCallback(
    (e) => {
      e?.preventDefault();
      const query = String(inputValue || '').trim();

      if (!query) {
        // Empty or whitespace-only requests are not submitted
        return;
      }

      if (isSmallScreen) setDrawerClose();
      onChangeText?.(query);
      onBlur();

      // Navigate only — EZSearch page effect owns interpret + composite fetch (avoids double call)
      if (isAiMode) {
        const targetSearch = `?mode=ai&q=${encodeURIComponent(query)}`;
        if (location.pathname !== ROUTE_EZ_SEARCH.path || location.search !== targetSearch) {
          history.push({ pathname: ROUTE_EZ_SEARCH.path, search: targetSearch });
        } else {
          onFetchAiSearch?.(query);
        }
      } else {
        const targetSearch = `?q=${encodeURIComponent(query)}`;
        if (location.pathname !== ROUTE_EZ_SEARCH.path || location.search !== targetSearch) {
          history.push({ pathname: ROUTE_EZ_SEARCH.path, search: targetSearch });
        }
      }
    },
    [inputValue, isSmallScreen, setDrawerClose, onChangeText, onBlur, isAiMode, location, history, onFetchAiSearch]
  );

  const handleRecentClick = useCallback(
    (searchQuery) => {
      setInputValue(searchQuery);
      if (isSmallScreen) setDrawerClose();
      onChangeText?.(searchQuery);
      onBlur();

      if (isAiMode) {
        const targetSearch = `?mode=ai&q=${encodeURIComponent(searchQuery)}`;
        if (location.pathname !== ROUTE_EZ_SEARCH.path || location.search !== targetSearch) {
          history.push({ pathname: ROUTE_EZ_SEARCH.path, search: targetSearch });
        } else {
          onFetchAiSearch?.(searchQuery);
        }
      } else {
        history.push({
          pathname: ROUTE_EZ_SEARCH.path,
          search: `?q=${encodeURIComponent(searchQuery)}`,
        });
      }
    },
    [isSmallScreen, setDrawerClose, onChangeText, onBlur, isAiMode, history, location, onFetchAiSearch]
  );

  useEffect(() => {
    fetchRecentSearchs?.();
  }, [fetchRecentSearchs]);

  const placeholder = isAiMode
    ? trans('ez-search-ai-placeholder') || 'Solar grants closing next month'
    : trans('ez-search') || 'Search across all categories';

  return (
    <>
      <form action="#" onSubmit={handleSubmit} className={styles.ezSearchForm} data-testid="ez-search-form">
        <div className={styles.ezSearchWrapper}>
          <span className={styles.ezSearchStartIcon}>
            {isAiMode ? (
              <SparklesIcon size={18} color="#2563eb" />
            ) : (
              <SearchIcon fontSize="small" style={{ color: '#94a3b8' }} />
            )}
          </span>

          <input
            type="text"
            name="ezSearch"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={placeholder}
            className={styles.ezSearchInput}
            onFocus={onFocus}
            autoComplete="off"
          />

          {nlEnabled && (
            <div
              className={styles.ezSearchToggleGroup}
              role="tablist"
              aria-label={trans('natural-language-search-modes')}
            >
              <button
                type="button"
                role="tab"
                aria-selected={!isAiMode}
                className={clsx(styles.ezSearchToggleBtn, !isAiMode && styles.ezSearchToggleBtnActive)}
                onClick={() => handleModeChange('keyword')}
                data-testid="ez-search-toggle-keyword"
              >
                <SearchIcon style={{ fontSize: 14 }} />
                <span>{trans('keyword')}</span>
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={isAiMode}
                className={clsx(styles.ezSearchToggleBtn, isAiMode && styles.ezSearchToggleBtnActive)}
                onClick={() => handleModeChange('ai')}
                data-testid="ez-search-toggle-ai"
              >
                <SparklesIcon size={13} color={isAiMode ? '#1d4ed8' : 'currentColor'} />
                <span>{trans('ask-ai')}</span>
              </button>
            </div>
          )}
        </div>
      </form>

      <BackDrop invisible open={focused} onClick={onBlur} />

      {focused && hasRecentSearches && !pending && (
        <Paper
          style={{ zIndex: 1001 }}
          className={styles.recentSearches}
          component={SpacingContainer}
          px={1}
          py={1}
          square
        >
          <Loader open={pending} size={30} />

          <List>
            <ListItem>
              <Typography variant="body2" weight={600}>
                {trans('RecentSearches')}
              </Typography>
            </ListItem>

            {recentSearches.map((item, ind) => (
              <ListItem key={ind}>
                <Typography
                  onClick={() => handleRecentClick(item.search)}
                  variant="body2"
                  color="primary"
                  className="pointer"
                >
                  {item.search}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </>
  );
}

EZSearch.propTypes = {
  text: PropTypes.string,
  searchMode: PropTypes.string,
  pending: PropTypes.bool,
  recentSearches: PropTypes.array,
  onChangeText: PropTypes.func,
  onSetSearchMode: PropTypes.func,
  onFetchAiSearch: PropTypes.func,
  fetchRecentSearchs: PropTypes.func,
  setDrawerClose: PropTypes.func,
};

export default connect(EZSearch);
